import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Award, Compass, GraduationCap, ArrowRight } from 'lucide-react';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import SearchBar from '@/components/SearchBar.jsx';
import FeaturedJobsCarousel from '@/components/FeaturedJobsCarousel.jsx';
import BrandGrid from '@/components/StoreGrid.jsx';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const navigate = useNavigate();

  const handleSearch = ({ keyword, location }) => {
    const params = new URLSearchParams();
    if (keyword) params.set('keyword', keyword);
    if (location) params.set('location', location);
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <>
      <Helmet>
        <title>Luxe Jobs London - Premium Careers in Luxury Retail & Beauty</title>
        <meta name="description" content="Discover exclusive career opportunities in London's most prestigious luxury retail stores and beauty brands. Find your dream role at Harrods, Selfridges, and more." />
      </Helmet>

      <Header />

      <main>
        <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(/assets/luxe-jobs-london-hero.jpg)',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-white mb-6">
                <span className="block">Your gateway to</span>
                <span className="block text-primary">luxury brand careers</span>
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-14 max-w-3xl mx-auto leading-relaxed tracking-wide font-light">
                Discover carefully selected opportunities in London's most prestigious retail and beauty destinations
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <SearchBar onSearch={handleSearch} />
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="mb-6">Featured luxury roles</h2>
              <p className="text-section-desc max-w-3xl mx-auto">
                Handpicked opportunities from London's most exclusive brands and high-end boutiques
              </p>
            </motion.div>

            <FeaturedJobsCarousel />
            <div className="mt-12 text-center">
              <Button asChild size="lg" className="px-8 py-6 text-base font-semibold">
                <Link to="/jobs">Browse all jobs</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/40 border-y border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="mb-6">Browse by Brand</h2>
              <p className="text-section-desc max-w-3xl mx-auto">
                Explore roles from leading luxury brands and destinations.
              </p>
            </motion.div>

            <BrandGrid />
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="mb-10">Why choose Luxe Jobs London</h2>
                <div className="space-y-10">
                  <div className="flex items-start space-x-6">
                    <div className="bg-primary/10 p-4 rounded-2xl shrink-0">
                      <Sparkles className="text-primary" size={32} />
                    </div>
                    <div>
                      <h3 className="mb-3">Curated opportunities</h3>
                      <p className="text-section-desc">
                        Explore relevant roles from recognised luxury brands, specialist recruiters and premium retailers.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-6">
                    <div className="bg-primary/10 p-4 rounded-2xl shrink-0">
                      <Compass className="text-primary" size={32} />
                    </div>
                    <div>
                      <h3 className="mb-3">Personalised career guidance</h3>
                      <p className="text-section-desc">
                        Explore suitable career paths or discover apprenticeships, trainee programmes and selected entry-level roles.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm font-semibold">
                        <Link to="/career-finder" className="group inline-flex items-center text-primary">Try Career Finder<ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
                        <Link to="/early-careers" className="group inline-flex items-center text-primary"><GraduationCap className="mr-1.5 h-4 w-4" />Explore Early Careers<ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-6">
                    <div className="bg-primary/10 p-4 rounded-2xl shrink-0">
                      <Award className="text-primary" size={32} />
                    </div>
                    <div>
                      <h3 className="mb-3">Practical career insights</h3>
                      <p className="text-section-desc">
                        Use salary insights, application guidance and interview advice to make better-informed career decisions.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-card border border-border rounded-3xl p-10 md:p-12 shadow-xl relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                  <Sparkles size={120} />
                </div>
                <div className="relative z-10">
                  <h3 className="mb-6">Never miss an opportunity</h3>
                  <p className="text-section-desc mb-10">
                    Get instant, tailored alerts when new luxury roles matching your precise preferences are posted.
                  </p>
                  <Link to="/job-alerts">
                    <Button size="lg" className="w-full text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 active:scale-[0.98] py-6">
                      Set up job alerts
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default HomePage;
